import { SensorType } from '../../types/sensor-type';
import { BaseSensorWebView } from '../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import _ from 'lodash';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { WEB_COMPONENT_GZ_LABELED_CONTENT, WEB_COMPONENT_GZ_TOOLTIP } from '../../../../webcomponents.constants';
import { htmlEncode } from '../../../../utils/escape.utils';
import { Sensor } from '../../../common/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzTextWebComponent } from '../../../../ui/gzText';

export class GoogleWorkspaceSensorView extends BaseSensorWebView {
  private static DOMAIN_KEY = 'domain';

  public formGroup: FormGroup;

  public dom: {
    $iss: GzFormFieldWebComponent;
    $adminEmail: GzFormFieldWebComponent;
    $domain: GzFormFieldWebComponent;
    $privateKey: GzFormFieldWebComponent;
    $adminLabel: GzTextWebComponent;
    $serviceLabel: GzTextWebComponent;
    $serviceAccountDetails: GzFormFieldWebComponent;
    $serviceAccountDetailsImportFileLabel: any;
    $serviceAccountDetailsButtonImportFile: GzButtonWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
        <div class="sensor-requirement" sensor-type="${SensorType.GOOGLE_WS}">
          <gz-title class="sensor-title">${dependencies.translateFn(
            'Before you integrate Google Workspace with GravityZone, make sure the following requirements are met:'
          )}
          </gz-title>
          <ol>
            <li>${dependencies.translateFn('Have a Google Workspace application and a service account available')}</li>
            <li>${dependencies.translateFn('Have the service account key exported in JSON format')}</li>
            <li>${dependencies.translateFn(
              'Have an Administrator account set up, for access to the Google Admin Console'
            )}</li>
            <li>${dependencies.translateFn(
              'For incident data enrichment, have the following scopes set up in the Manage domain-wide delegation section:'
            )}
              <ul>
                <li>
                  https://www.googleapis.com/auth/admin.directory.user.readonly
                </li>
                <li>
                  https://www.googleapis.com/auth/admin.reports.audit.readonly
                </li>
                <li>
                  https://www.googleapis.com/auth/gmail.readonly
                </li>
                <li>
                  https://www.googleapis.com/auth/drive.readonly
                </li>
                <li>
                  https://www.googleapis.com/auth/admin.directory.domain.readonly
                </li>
                <li>
                  https://www.googleapis.com/auth/admin.directory.device.chromeos.readonly
                </li>
              </ul>
            </li>
             <li>${dependencies.translateFn(
              'Optionally, for incident response capabilities, have these additional scopes set up:'
            )}
              <ul>
                <li>
                  https://www.googleapis.com/auth/admin.directory.user
                </li>
                <li>
                  https://www.googleapis.com/auth/admin.directory.user.security
                </li>
              </ul>
            </li>
          </ol>
          <p>${dependencies.translateFn(
            'For a complete Google Workspace sensor integration guide, refer to {detailLink}.',
            {
              detailLink:
                '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77211-452349-the-google-workspace-sensor.html"><span>' +
                dependencies.translateFn('this KB article') +
                '</span></gz-link>',
            }
          )}</p>
        </div>
        `;
  }

  public static getSensorSpecifics(sensor: Sensor, dependencies: any) {
    let result = ``;
    if (sensor.specifics.domain) {
      result += GoogleWorkspaceSensorView.buildSensorDetailInfo(
        GoogleWorkspaceSensorView.DOMAIN_KEY,
        sensor.specifics.domain,
        dependencies
      );
    }

    return result;
  }

  private static buildSensorDetailInfo(key: string, value: string, dependencies: any): string {
    if (key === GoogleWorkspaceSensorView.DOMAIN_KEY) {
      const label = dependencies.translateFn('Domain');
      return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${label}" hasColon="true">
                <${WEB_COMPONENT_GZ_TOOLTIP} class="tooltip-subscriptionId" tooltipText="${htmlEncode(value)}">
                    ${value}
                </${WEB_COMPONENT_GZ_TOOLTIP}>
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
    }

    return '';
  }

  protected createFormGroup() {
    this.dom.$iss.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.Iss.MaxLength,
        this.dependencies.translateFn('The client email must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.Iss.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The client email cannot contain spaces.')),
    ];

    this.dom.$privateKey.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.dom.$adminEmail.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.AdminEmail.MaxLength,
        this.dependencies.translateFn('The admin email must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.AdminEmail.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The admin email cannot contain spaces.')),
      RegexValidator(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        this.dependencies.translateFn('The admin email must have a valid email format.')
      ),
    ];

    this.dom.$domain.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.Domain.MaxLength,
        this.dependencies.translateFn('The domain must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.Domain.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The domain cannot contain spaces.')),
    ];

    this.formGroup = new FormGroup({
      iss: this.dom.$iss,
      admin_email: this.dom.$adminEmail,
      domain: this.dom.$domain,
      private_key: this.dom.$privateKey,
    });
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $iss: this.shadow.querySelector('#google_workspace_iss'),
      $adminEmail: this.shadow.querySelector('#google_workspace_admin_email'),
      $domain: this.shadow.querySelector('#google_workspace_domain'),
      $privateKey: this.shadow.querySelector('#google_workspace_private_key'),
      $adminLabel: this.shadow.querySelector('#google_workspace_admin_label'),
      $serviceLabel: this.shadow.querySelector('#google_workspace_service_label'),
      $serviceAccountDetails: this.shadow.querySelector('#google_workspace_sa_details'),
      $serviceAccountDetailsImportFileLabel: this.shadow.querySelector('#label_for_google_workspace_sa_details_file'),
      $serviceAccountDetailsButtonImportFile: this.shadow.querySelector('#google_workspace_sa_details_btn_import_file'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  setTranslatedStrings(): void {
    this.dom.$iss.label = this.dependencies.translateFn('Client email');
    this.dom.$privateKey.label = this.dependencies.translateFn('Private Key');
    this.dom.$adminEmail.label = this.dependencies.translateFn('Admin email');
    this.dom.$domain.label = this.dependencies.translateFn('Domain');
    this.dom.$adminLabel.innerText = this.dependencies.translateFn('Administrator account details');
    this.dom.$serviceLabel.innerText = this.dependencies.translateFn('Service account details');
    this.dom.$serviceAccountDetailsImportFileLabel.innerText = this.dependencies.translateFn(
      'Import the JSON file with your service account details, or manually fill out the fields below.'
    );
    this.dom.$serviceAccountDetailsButtonImportFile.innerText = this.dependencies.translateFn('Import file');
    this.setBaseTranslatedStrings(SensorType.GOOGLE_WS);
  }

  protected async setViewData(viewData) {
    super.setViewData(viewData);
    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        iss: _.get(viewData, ['response', 'configuration', 'iss'], ''),
        admin_email: _.get(viewData, ['response', 'configuration', 'admin_email'], ''),
        domain: _.get(viewData, ['response', 'configuration', 'domain'], ''),
        private_key: _.get(viewData, ['response', 'configuration', 'private_key'], ''),
      };
    }
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }
}
